/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { motion } from "framer-motion";
import { Sprout, Snowflake, ShieldCheck, HeartHandshake, ScanLine, CheckCircle2, ArrowUpRight } from "lucide-react";
import ChapterHeading from "./ChapterHeading";
import AppStoreBadges from "../brand/AppStoreBadges";
import { fadeUp, staggerParent, viewportOnce } from "../../lib/motion";

const SMARTTRACE_URL = "https://trace-passport-ci.preview.emergentagent.com/";

const pillars = [
  { icon: Sprout, color: "#68AD45", title: "Origine vérifiée", line: "Du champ au quartier, chaque étape est enregistrée." },
  { icon: Snowflake, color: "#524FA1", title: "Fraîcheur garantie", line: "La chaîne du froid est suivie, sans rupture cachée." },
  { icon: ShieldCheck, color: "#ED1C24", title: "Sécurité alimentaire", line: "En cas d'alerte, le lot concerné est isolé en un instant." },
  { icon: HeartHandshake, color: "#231F20", title: "Commerce équitable", line: "Une juste rémunération, prouvée et transparente." },
];

// Decorative pseudo-QR (11x11) with finder squares — purely illustrative.
const QR = [
  "11111011111",
  "10001010001",
  "10111010111",
  "10111000101",
  "10001011101",
  "11111010101",
  "00000011000",
  "11101101011",
  "10101000111",
  "00110110101",
  "11101011111",
];

function QrMark() {
  return (
    <svg viewBox="0 0 11 11" className="h-24 w-24" shapeRendering="crispEdges" aria-hidden="true">
      <rect width="11" height="11" fill="#fff" />
      {QR.flatMap((row, y) =>
        row.split("").map((c, x) =>
          c === "1" ? <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="#231F20" /> : null
        )
      )}
    </svg>
  );
}

export default function Tracabilite() {
  return (
    <section id="tracabilite" data-testid="tracabilite-section" className="relative bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: text */}
          <div>
            <ChapterHeading number="04" kicker="Traçabilité" title="Chaque produit Diallo, traçable." />
            <motion.p
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              className="mt-6 text-lg md:text-xl leading-relaxed text-diallo-ink/80"
            >
              Grâce à notre partenariat avec <span className="font-semibold text-diallo-charcoal">SmartTrace</span>,
              les produits sensibles vendus chez Diallo reçoivent une identité numérique unique et infalsifiable.
              Origine, fraîcheur, sécurité et équité : tout se vérifie en trois secondes, d'un simple scan, sur
              n'importe quel téléphone.
            </motion.p>

            <motion.div
              variants={staggerParent}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
            >
              {pillars.map((p) => {
                const Icon = p.icon;
                return (
                  <motion.div key={p.title} variants={fadeUp} data-testid={`trace-pillar-${p.title}`} className="flex gap-4">
                    <span
                      className="mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                      style={{ backgroundColor: `${p.color}15` }}
                    >
                      <Icon size={22} color={p.color} strokeWidth={2.2} />
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-lg text-diallo-charcoal">{p.title}</h3>
                      <p className="mt-1 text-diallo-ink/70 text-sm leading-relaxed">{p.line}</p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.a
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              href={SMARTTRACE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tracabilite-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-xl bg-diallo-red px-8 py-4 font-semibold text-white shadow-lg shadow-diallo-red/20 transition-transform hover:scale-[1.03] active:scale-95"
            >
              Découvrir SmartTrace
              <ArrowUpRight size={20} strokeWidth={2.4} />
            </motion.a>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              className="mt-10 rounded-2xl bg-diallo-sand/70 p-6"
            >
              <p className="font-display font-bold text-diallo-charcoal">Scannez, vérifiez, achetez en confiance.</p>
              <p className="mt-1 text-sm text-diallo-ink/70">Téléchargez l'application pour vérifier vos produits en un scan.</p>
              <AppStoreBadges className="mt-4" />
            </motion.div>
          </div>

          {/* Right: product passport card */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="relative"
          >
            <div className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-diallo-sand/70" />
            <div
              data-testid="trace-passport-card"
              className="relative rounded-3xl bg-white p-7 md:p-8 shadow-[0_20px_60px_rgb(0,0,0,0.12)]"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-diallo-ink/50">
                  <ScanLine size={16} className="text-diallo-red" />
                  Passeport produit
                </span>
                <span className="text-xs font-semibold text-diallo-green">Vérifié</span>
              </div>

              <div className="mt-6 flex items-center gap-5">
                <div className="rounded-2xl border border-black/5 p-2 shadow-sm">
                  <QrMark />
                </div>
                <div>
                  <p className="font-display font-bold text-xl text-diallo-charcoal leading-tight">Mangue Kent</p>
                  <p className="text-diallo-ink/60">Origine : Côte d'Ivoire</p>
                  <p className="mt-1 text-sm text-diallo-ink/50">Lot #CI-2026-04871</p>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {[
                  "Récolte enregistrée à la ferme",
                  "Chaîne du froid respectée",
                  "Contrôle qualité validé",
                  "Mise en rayon — boutique Diallo",
                ].map((step) => (
                  <div key={step} className="flex items-center gap-3">
                    <CheckCircle2 size={18} className="text-diallo-green shrink-0" />
                    <span className="text-sm text-diallo-ink/80">{step}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-2xl bg-diallo-charcoal px-5 py-4 text-white flex items-center justify-between">
                <span className="text-sm text-white/70">Vérifié en</span>
                <span className="font-display font-extrabold text-2xl">3 secondes</span>
              </div>

              <p className="mt-4 text-center text-xs text-diallo-ink/40">
                Illustration — propulsé par SmartTrace
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
